import { useContext, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck2, ChevronLeft, ChevronRight, CloudOff, Loader2, RefreshCw, XCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AdminPageHeader } from "./components/AdminUi";
import { VisiteChromeContext } from "../../components/visite/chrome";
import { ESITI_VISITA, STATI_VISITA, formatRome, statoLabel, toRomeInput, visitSummary } from "../../lib/visite/visiteFormat";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/admin/commerciale/visite`;
const STEPS = ["Cliente", "Intervista", "Servizi", "Note", "Esito", "Riepilogo"];
const INTERVIEW_FIELDS = [
  ["gestionale_attuale", "Gestionale attuale"],
  ["criticita", "Criticità emerse"],
  ["decisore", "Decisore e partecipanti"],
  ["tempi", "Tempi decisionali"],
  ["budget", "Budget / condizioni"],
  ["moduli_interesse", "Moduli Affidya di interesse"],
];
const MAX_TEXT = 2000;
const MAX_REFERENTI = 20;
const RETRY_MS = 20_000;
const EMPTY = {
  data_visita: "", referenti_incontrati: [], intervista: {}, servizi_interesse: [], note: "",
  esito: "", prossimo_appuntamento: "", prossimo_passo: "",
};
const DEFAULT_PERMS = { scrittura: false, vedi_tutti: false, riassegna: false };

function errorText(json, fallback) {
  if (typeof json?.detail === "string") return json.detail;
  if (Array.isArray(json?.detail) && json.detail[0]?.msg) return `Dati non validi: ${json.detail[0].msg}`;
  return fallback;
}

function localKey(id) {
  return `affidya:visita:bozza:${id}`;
}

/** Solo i campi modificabili, nel formato atteso dall'API (date in ora di Roma, senza fuso). */
export function visitPayload(form) {
  const intervista = {};
  INTERVIEW_FIELDS.forEach(([key]) => {
    if (form.intervista?.[key]) intervista[key] = String(form.intervista[key]).slice(0, MAX_TEXT);
  });
  return {
    data_visita: form.data_visita || undefined,
    referenti_incontrati: (form.referenti_incontrati || []).slice(0, MAX_REFERENTI).map((r) => ({ nome: String(r.nome || r).slice(0, 200) })).filter((r) => r.nome),
    intervista,
    servizi_interesse: form.servizi_interesse || [],
    note: form.note || "",
    esito: form.esito || "",
    prossimo_appuntamento: form.prossimo_appuntamento || "",
    prossimo_passo: form.prossimo_passo || "",
  };
}

function formFromVisit(visit) {
  return {
    ...EMPTY,
    ...visit,
    data_visita: toRomeInput(visit.data_visita),
    prossimo_appuntamento: toRomeInput(visit.prossimo_appuntamento),
  };
}

function StatoBadge({ stato, big }) {
  const meta = STATI_VISITA[stato] || { className: "bg-slate-100 text-slate-700" };
  return <span className={`shrink-0 whitespace-nowrap rounded-full ${big ? "px-3 py-1 text-sm" : "px-2 py-0.5 text-xs"} ${meta.className}`}>{statoLabel(stato)}</span>;
}

export default function CommercialeVisiteModule({ layout = "admin" }) {
  const big = layout === "app";
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [visits, setVisits] = useState([]);
  const [perms, setPerms] = useState(DEFAULT_PERMS);
  const [filters, setFilters] = useState({ q: "", stato: "", responsabile: "" });
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sync, setSync] = useState("ok"); // ok | offline | conflict
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const timer = useRef(null);
  const visibleVisitId = useRef(null);
  const deepLinkDone = useRef(false);
  const setChrome = useContext(VisiteChromeContext);
  visibleVisitId.current = selected?.id || null;
  const readOnly = !selected?.can_edit;

  const load = useCallback(async (current = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(current).forEach(([key, value]) => { if (value) params.set(key, value); });
      const res = await fetch(`${BASE}${params.toString() ? `?${params}` : ""}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorText(json, "Impossibile caricare le visite"));
      setVisits(json.data || []);
      setPerms({ ...DEFAULT_PERMS, ...(json.permessi || {}) });
    } catch (err) {
      toast.error(err.message || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handle = window.setTimeout(() => load(filters), filters.q ? 300 : 0);
    return () => window.clearTimeout(handle);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    if (!perms.riassegna) return;
    fetch(`${BASE}/responsabili`, { headers })
      .then((res) => (res.ok ? res.json() : { data: [] }))
      .then((json) => setAssignees(json.data || []))
      .catch(() => setAssignees([]));
  }, [perms.riassegna]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setMatches([]);
      setSearching(false);
      return undefined;
    }
    const handle = window.setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${BASE}/strutture?${new URLSearchParams({ q: term })}`, { headers });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(errorText(json, "Impossibile cercare le strutture"));
        setMatches((json.data || []).slice(0, 8));
      } catch (err) {
        setMatches([]);
        toast.error(err.message || "Errore ricerca strutture");
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistLocal = (visit, nextForm, nextStep = step) => {
    if (visit?.id) localStorage.setItem(localKey(visit.id), JSON.stringify({ savedAt: Date.now(), form: nextForm, step: nextStep }));
  };

  const setField = (key, value) => {
    if (readOnly) return;
    setForm((current) => {
      const next = { ...current, [key]: value };
      persistLocal(selected, next);
      return next;
    });
  };
  const setInterview = (key, value) => setField("intervista", { ...(form.intervista || {}), [key]: value });

  /** Salva la bozza. Il passo cambia comunque: se il server non risponde la copia resta sul tablet. */
  const save = async (nextStep = step) => {
    if (!selected?.id || readOnly) {
      setStep(nextStep);
      return true;
    }
    const visitId = selected.id;
    setStep(nextStep);
    persistLocal(selected, form, nextStep);
    if (sync === "conflict") return false;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/${visitId}`, {
        method: "PATCH", headers,
        body: JSON.stringify({ ...visitPayload(form), expected_version: selected.version }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setSync("conflict");
        return false;
      }
      if (!res.ok) throw new Error(errorText(json, "Salvataggio bozza non riuscito"));
      localStorage.removeItem(localKey(visitId));
      if (visibleVisitId.current === visitId) {
        setSelected((current) => ({ ...current, version: json.version }));
        setSync("ok");
      }
      return true;
    } catch (err) {
      if (visibleVisitId.current === visitId) setSync(err instanceof TypeError ? "offline" : "invalid");
      if (!(err instanceof TypeError)) toast.error(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Nuovo tentativo automatico finché la bozza non è sincronizzata.
  useEffect(() => {
    if (!selected || readOnly || sync !== "offline") return undefined;
    const handle = window.setInterval(() => save(step), RETRY_MS);
    return () => window.clearInterval(handle);
  }, [selected?.id, sync, step, form]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected || readOnly) return undefined;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => save(step), RETRY_MS);
    return () => window.clearTimeout(timer.current);
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  const leaveVisit = () => {
    window.clearTimeout(timer.current);
    if (selected && !readOnly && sync !== "conflict") save(step);
    visibleVisitId.current = null;
    setSelected(null);
    setStep(0);
    setSync("ok");
    {
      const url = new URL(window.location.href);
      if (url.searchParams.has("visita")) {
        url.searchParams.delete("visita");
        window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      }
    }
    load();
  };

  const open = async (visitOrId) => {
    const id = typeof visitOrId === "string" ? visitOrId : visitOrId.id;
    try {
      const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorText(json, "Impossibile aprire la visita"));
      let nextForm = formFromVisit(json);
      let nextStep = json.can_edit ? 0 : STEPS.length - 1;
      const local = json.can_edit ? localStorage.getItem(localKey(json.id)) : null;
      if (local) {
        const cached = JSON.parse(local);
        const serverAt = new Date(json.updated_at || 0).getTime();
        if (cached.savedAt > serverAt && window.confirm("È disponibile una copia locale più recente della bozza. Ripristinarla?")) {
          nextForm = { ...nextForm, ...(cached.form || {}) };
          nextStep = cached.step || 0;
        } else {
          localStorage.removeItem(localKey(json.id));
        }
      }
      setSelected(json);
      setForm(nextForm);
      setStep(nextStep);
      setSync("ok");
      setAssignTo("");
    } catch (err) {
      toast.error(err.message || "Errore apertura visita");
    }
  };

  // Deep link dalle notifiche: ?visita=<id>
  useEffect(() => {
    if (deepLinkDone.current) return;
    deepLinkDone.current = true;
    const id = new URLSearchParams(window.location.search).get("visita");
    if (id) open(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadFromServer = async () => {
    if (!selected) return;
    await open(selected.id);
  };

  const create = async (strutturaId) => {
    if (!strutturaId || saving) return;
    setSaving(true);
    try {
      const res = await fetch(BASE, { method: "POST", headers, body: JSON.stringify({ struttura_id: strutturaId }) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorText(json, "Impossibile creare la visita"));
      setQuery("");
      setMatches([]);
      if (json.esistente) {
        toast.info("Hai già una bozza aperta per questa struttura: l'ho riaperta.");
        await open(json.visita.id);
        return;
      }
      setSelected(json.visita);
      setForm(formFromVisit(json.visita));
      setStep(0);
      setSync("ok");
      setVisits((current) => [json.visita, ...current]);
    } catch (err) {
      toast.error(err.message || "Errore creazione visita");
    } finally {
      setSaving(false);
    }
  };

  const close = async () => {
    if (!selected || readOnly) return;
    if (!form.esito) {
      toast.error("Indica l'esito della visita prima di chiuderla.");
      setStep(4);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/${selected.id}/chiudi`, {
        method: "POST", headers, body: JSON.stringify({ ...visitPayload(form), expected_version: selected.version }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 409) setSync("conflict");
      if (!res.ok) throw new Error(errorText(json, "Chiusura visita non riuscita"));
      localStorage.removeItem(localKey(selected.id));
      toast.success(json.prospect_id ? "Visita chiusa e opportunità Affidya collegata" : "Visita chiusa");
      visibleVisitId.current = null;
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.message || "Errore chiusura visita");
    } finally {
      setSaving(false);
    }
  };

  const cancelVisit = async () => {
    if (!selected || readOnly) return;
    if (cancelReason.trim().length < 3) {
      toast.error("Indica il motivo dell'annullamento");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/${selected.id}/annulla`, {
        method: "POST", headers, body: JSON.stringify({ expected_version: selected.version, motivo: cancelReason.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 409) setSync("conflict");
      if (!res.ok) throw new Error(errorText(json, "Annullamento non riuscito"));
      localStorage.removeItem(localKey(selected.id));
      toast.success("Visita annullata");
      setCancelOpen(false);
      setCancelReason("");
      visibleVisitId.current = null;
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.message || "Errore annullamento");
    } finally {
      setSaving(false);
    }
  };

  const reassign = async () => {
    if (!selected || !assignTo) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/${selected.id}/riassegna`, {
        method: "POST", headers, body: JSON.stringify({ expected_version: selected.version, email: assignTo }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 409) setSync("conflict");
      if (!res.ok) throw new Error(errorText(json, "Riassegnazione non riuscita"));
      setSelected((current) => ({ ...current, version: json.version, responsabile: json.responsabile }));
      toast.success(`Visita assegnata a ${json.responsabile?.nome || assignTo}`);
      setAssignTo("");
    } catch (err) {
      toast.error(err.message || "Errore riassegnazione");
    } finally {
      setSaving(false);
    }
  };

  const syncLabel = saving ? "Salvataggio…" : (sync === "ok" ? "" : "Non sincronizzata");

  useEffect(() => {
    if (layout !== "app" || !setChrome) return undefined;
    if (!selected) {
      setChrome({
        title: "Le mie visite",
        subtitle: loading ? "Caricamento" : (visits.length ? `${visits.length} in elenco` : "Nessuna visita"),
        onBack: null,
      });
      return undefined;
    }
    setChrome({
      title: selected.struttura_nome || "Visita",
      subtitle: [statoLabel(selected.stato), STEPS[step], syncLabel].filter(Boolean).join(" · "),
      onBack: leaveVisit,
    });
    return undefined;
  }, [layout, selected, step, saving, sync, loading, visits.length, setChrome]); // eslint-disable-line react-hooks/exhaustive-deps

  const field = big ? "h-12 text-base md:text-base" : "min-h-11";
  const labelCls = big ? "text-base" : "";
  const target = big ? "h-14 text-base" : "min-h-11";

  if (selected) {
    const structureName = selected.struttura_nome || "Struttura";
    const owner = selected.responsabile || {};
    return (
      <div className={big ? "space-y-4" : "mx-auto max-w-3xl space-y-4 pb-8"} data-testid="visita-dettaglio">
        {!big ? (
          <AdminPageHeader
            icon={CalendarCheck2}
            title={selected.stato === "bozza" ? "Visita in corso" : "Visita"}
            description={`${structureName} · ${STEPS[step]}`}
            actions={<Button variant="outline" className="min-h-11" onClick={leaveVisit}><ChevronLeft className="mr-1 h-4 w-4" /> Elenco</Button>}
          />
        ) : null}
        <div className="flex flex-wrap items-center gap-2 text-sm text-admin-muted">
          <StatoBadge stato={selected.stato} big={big} />
          <span>Responsabile: {owner.nome || owner.email || "—"}</span>
          {syncLabel ? <span className="font-medium text-amber-700">{syncLabel}</span> : null}
        </div>
        {readOnly ? (
          <div role="status" className="rounded-2xl border border-admin-border bg-slate-50 px-4 py-3 text-sm text-slate-700" data-testid="visita-sola-lettura">
            {selected.stato === "bozza"
              ? "Sola lettura: questa visita è assegnata a un altro commerciale."
              : `Sola lettura: la visita è ${statoLabel(selected.stato).toLowerCase()}.`}
          </div>
        ) : null}
        {sync === "offline" || sync === "invalid" ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="visita-non-sincronizzata">
            <span className="flex items-center gap-2"><CloudOff className="h-4 w-4" /> {sync === "offline" ? "Connessione assente: le modifiche restano sul tablet e vengono ritentate in automatico." : "Alcuni dati non sono stati accettati: correggili e riprova."}</span>
            <Button variant="outline" className="min-h-11" onClick={() => save(step)} disabled={saving}>Riprova ora</Button>
          </div>
        ) : null}
        {sync === "conflict" ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900" data-testid="visita-conflitto">
            <span>La bozza è stata modificata da un altro dispositivo. La tua copia è salvata sul tablet: ricarica per scegliere quale tenere.</span>
            <Button variant="outline" className="min-h-11" onClick={reloadFromServer}>Ricarica</Button>
          </div>
        ) : null}
        <div className={big ? "grid grid-cols-3 gap-2 sm:grid-cols-6" : "flex gap-1 overflow-x-auto"}>
          {STEPS.map((label, index) => (
            <button
              type="button"
              key={label}
              aria-current={index === step ? "step" : undefined}
              className={big
                ? `min-h-14 rounded-2xl px-2 text-sm font-medium leading-tight ${index === step ? "bg-admin-accent text-white" : "border border-admin-border bg-admin-surface text-admin-muted"}`
                : `min-h-11 min-w-max rounded-full px-4 text-sm ${index === step ? "bg-admin-accent text-white" : "bg-slate-100 text-slate-600"}`}
              onClick={() => save(index)}
            >
              {big ? <span className="block text-xs opacity-80">{index + 1}</span> : `${index + 1}. `}
              {label}
            </button>
          ))}
        </div>
        <Card className={big ? "rounded-3xl border-admin-border bg-admin-surface shadow-sm" : ""}>
          <CardContent className="space-y-4 p-4 sm:p-6">
            <fieldset disabled={readOnly} className="space-y-4">
              {step === 0 && (
                <>
                  <div><Label className={labelCls}>Cliente</Label><Input className={`mt-2 ${field}`} value={structureName} disabled readOnly /></div>
                  <div><Label className={labelCls} htmlFor="visita-data">Data e ora visita (ora italiana)</Label><Input id="visita-data" className={`mt-2 ${field}`} type="datetime-local" value={form.data_visita || ""} onChange={(e) => setField("data_visita", e.target.value)} /></div>
                  <div><Label className={labelCls} htmlFor="visita-referenti">Referenti incontrati</Label><Input id="visita-referenti" className={`mt-2 ${field}`} placeholder="Nome e ruolo, separati da virgola" value={(form.referenti_incontrati || []).map((x) => x.nome || x).join(", ")} onChange={(e) => setField("referenti_incontrati", e.target.value.split(",").map((nome) => ({ nome: nome.trim().slice(0, 200) })).filter((x) => x.nome).slice(0, MAX_REFERENTI))} /></div>
                </>
              )}
              {step === 1 && INTERVIEW_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <Label className={labelCls} htmlFor={`intervista-${key}`}>{label}</Label>
                  <Textarea id={`intervista-${key}`} className={big ? "mt-2 min-h-24 text-base md:text-base" : "mt-2"} rows={big ? 3 : 2} maxLength={MAX_TEXT} value={form.intervista?.[key] || ""} onChange={(e) => setInterview(key, e.target.value)} />
                </div>
              ))}
              {step === 2 && (
                <div className="space-y-3">
                  <p className="text-sm text-admin-muted">Seleziona i servizi che interessano la struttura. La partnership CDRPT verrà collegata nel secondo blocco.</p>
                  {["AFFIDYA", "CDRPT"].map((service) => (
                    <label className="flex min-h-14 items-center gap-3 rounded-2xl border border-admin-border px-4 text-base" key={service}>
                      <input className="h-5 w-5" type="checkbox" checked={(form.servizi_interesse || []).includes(service)} onChange={(e) => setField("servizi_interesse", e.target.checked ? [...new Set([...(form.servizi_interesse || []), service])] : (form.servizi_interesse || []).filter((item) => item !== service))} />
                      {service}
                    </label>
                  ))}
                </div>
              )}
              {step === 3 && (
                <div><Label className={labelCls} htmlFor="visita-note">Note della visita</Label><Textarea id="visita-note" className={big ? "mt-2 min-h-56 text-base md:text-base" : "mt-2"} rows={9} maxLength={10000} value={form.note || ""} onChange={(e) => setField("note", e.target.value)} /></div>
              )}
              {step === 4 && (
                <>
                  <div>
                    <Label className={labelCls} htmlFor="visita-esito">Esito *</Label>
                    <select id="visita-esito" className={`mt-2 block w-full rounded-md border border-admin-border bg-transparent px-3 ${field}`} value={form.esito || ""} onChange={(e) => setField("esito", e.target.value)}>
                      <option value="">Seleziona esito</option>
                      {Object.entries(ESITI_VISITA).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </div>
                  <div><Label className={labelCls} htmlFor="visita-follow">Prossimo appuntamento (ora italiana)</Label><Input id="visita-follow" className={`mt-2 ${field}`} type="datetime-local" value={form.prossimo_appuntamento || ""} onChange={(e) => setField("prossimo_appuntamento", e.target.value)} /></div>
                  <div><Label className={labelCls} htmlFor="visita-passo">Prossimo passo</Label><Input id="visita-passo" className={`mt-2 ${field}`} maxLength={1000} value={form.prossimo_passo || ""} onChange={(e) => setField("prossimo_passo", e.target.value)} placeholder="Es. inviare proposta e richiamare" /></div>
                </>
              )}
            </fieldset>
            {step === 5 && (
              <dl className={`grid gap-2 ${big ? "text-base" : "text-sm"}`} data-testid="visita-riepilogo">
                <div><dt className="font-semibold">Cliente</dt><dd>{structureName}</dd></div>
                <div><dt className="font-semibold">Data visita</dt><dd>{formatRome(form.data_visita) || "Da definire"}</dd></div>
                <div><dt className="font-semibold">Referenti</dt><dd>{(form.referenti_incontrati || []).map((x) => x.nome || x).join(", ") || "—"}</dd></div>
                {INTERVIEW_FIELDS.filter(([key]) => form.intervista?.[key]).map(([key, label]) => (
                  <div key={key}><dt className="font-semibold">{label}</dt><dd className="whitespace-pre-line">{form.intervista[key]}</dd></div>
                ))}
                <div><dt className="font-semibold">Servizi</dt><dd>{(form.servizi_interesse || []).join(", ") || "Nessuno"}</dd></div>
                <div><dt className="font-semibold">Note</dt><dd className="whitespace-pre-line">{form.note || "—"}</dd></div>
                <div><dt className="font-semibold">Esito</dt><dd>{ESITI_VISITA[form.esito] || "Da definire"}</dd></div>
                <div><dt className="font-semibold">Follow-up</dt><dd>{form.prossimo_appuntamento ? `${formatRome(form.prossimo_appuntamento)}${form.prossimo_passo ? ` · ${form.prossimo_passo}` : ""}` : "Nessuno"}</dd></div>
                {selected.motivo_annullamento ? <div><dt className="font-semibold">Motivo annullamento</dt><dd>{selected.motivo_annullamento}</dd></div> : null}
                {(selected.storico || []).length ? (
                  <div><dt className="font-semibold">Storico</dt><dd><ul className="list-disc pl-5">{selected.storico.slice(-10).map((h) => <li key={h.id}>{formatRome(h.at)} · {h.testo}</li>)}</ul></dd></div>
                ) : null}
              </dl>
            )}
            {perms.riassegna && selected.stato === "bozza" ? (
              <div className="flex flex-wrap items-end gap-2 border-t pt-4" data-testid="visita-riassegna">
                <div className="min-w-48 flex-1">
                  <Label htmlFor="visita-assegna">Riassegna a</Label>
                  <select id="visita-assegna" className="mt-1 block min-h-11 w-full rounded-md border border-admin-border bg-transparent px-3" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                    <option value="">Scegli commerciale</option>
                    {assignees.filter((u) => u.email !== owner.email).map((u) => <option key={u.email} value={u.email}>{u.nome}</option>)}
                  </select>
                </div>
                <Button variant="outline" className="min-h-11" disabled={!assignTo || saving} onClick={reassign}>Riassegna</Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
        <div className={big ? "sticky bottom-0 z-20 -mx-4 border-t border-admin-border bg-admin-bg/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6" : "flex flex-wrap justify-between gap-2"}>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className={`${target} flex-1 [&_svg]:size-5`} disabled={step === 0} onClick={() => save(step - 1)}>
              <ChevronLeft className="h-5 w-5" /> Indietro
            </Button>
            {step < STEPS.length - 1 ? (
              <Button className={`${target} flex-[1.4] [&_svg]:size-5`} onClick={() => save(step + 1)}>
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Avanti <ChevronRight className="h-5 w-5" />
              </Button>
            ) : !readOnly ? (
              <Button className={`${target} flex-[1.4] [&_svg]:size-5`} disabled={saving || sync === "conflict"} onClick={close}>
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <CalendarCheck2 className="h-5 w-5" />} Chiudi visita
              </Button>
            ) : null}
          </div>
          {!readOnly ? (
            <Button variant="ghost" className={`${big ? "mt-2 h-12" : "min-h-11"} w-full text-rose-700 hover:bg-rose-50 hover:text-rose-800 sm:w-auto`} disabled={saving} onClick={() => setCancelOpen(true)}>
              <XCircle className="mr-1 h-4 w-4" /> Annulla visita
            </Button>
          ) : null}
        </div>
        <Dialog open={cancelOpen} onOpenChange={(value) => { setCancelOpen(value); if (!value) setCancelReason(""); }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Annullare la visita?</DialogTitle></DialogHeader>
            <p className="text-sm text-admin-muted">La visita a {structureName} resterà nello storico come annullata e non sarà più modificabile.</p>
            <Label htmlFor="visita-motivo">Motivo *</Label>
            <Textarea id="visita-motivo" rows={3} maxLength={1000} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Es. appuntamento rinviato dalla struttura" />
            <DialogFooter>
              <Button variant="outline" className="min-h-11" onClick={() => setCancelOpen(false)}>Indietro</Button>
              <Button className="min-h-11 bg-rose-600 text-white hover:bg-rose-700" disabled={saving || cancelReason.trim().length < 3} onClick={cancelVisit}>Conferma annullamento</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const responsabili = [...new Map(visits.map((v) => [v.responsabile?.email, v.responsabile]).filter(([key]) => key)).values()];

  return (
    <div className={big ? "space-y-5" : "space-y-4"}>
      {!big ? (
        <AdminPageHeader icon={CalendarCheck2} title="Le mie visite" description="Visite alle strutture, bozze recuperabili, opportunità Affidya e follow-up." actions={<Button variant="outline" className="min-h-11" onClick={() => load()} disabled={loading}><RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna</Button>} />
      ) : null}
      {perms.scrittura ? (
        <Card className={big ? "rounded-3xl border-admin-border bg-admin-surface shadow-sm" : ""}>
          <CardContent className={big ? "p-5" : "p-4"}>
            <div className="flex items-end justify-between gap-3">
              <Label htmlFor="nuova-visita-struttura" className={labelCls}>Nuova visita · struttura</Label>
              {big ? <Button variant="outline" className="h-12 px-4 text-base [&_svg]:size-5" onClick={() => load()} disabled={loading}><RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} /> Aggiorna</Button> : null}
            </div>
            <Input id="nuova-visita-struttura" className={`mt-2 ${field}`} value={query} placeholder="Cerca per nome, comune o provincia" disabled={saving} onChange={(event) => setQuery(event.target.value)} />
            <p className="mt-2 text-sm text-admin-muted">Digita almeno due lettere. La scelta crea subito una bozza, recuperabile in qualunque momento.</p>
            {query.trim().length >= 2 ? (
              <div className="mt-2 max-h-72 overflow-y-auto rounded-2xl border border-admin-border" data-testid="visite-risultati-strutture">
                {searching ? <p className="p-3 text-sm text-admin-muted">Ricerca in corso…</p> : null}
                {!searching && !matches.length ? <p className="p-3 text-sm text-admin-muted">Nessuna struttura trovata.</p> : null}
                {matches.map((item) => (
                  <button type="button" key={item.id} className="block min-h-12 w-full border-b border-admin-border px-4 py-3 text-left text-admin-text last:border-0 hover:bg-admin-accent-soft/40" disabled={saving} onClick={() => create(item.id)}>
                    <span className="font-medium">{item.name}</span>
                    {item.comune ? <span className="text-admin-muted">{` · ${item.comune}`}</span> : null}
                    {item.provincia ? <span className="text-admin-muted">{` (${item.provincia})`}</span> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
      <div className="flex flex-wrap gap-2" data-testid="visite-filtri">
        <Input aria-label="Cerca visita per struttura" className={`min-w-48 flex-1 ${field}`} placeholder="Cerca struttura" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
        <select aria-label="Filtra per stato" className={`rounded-md border border-admin-border bg-transparent px-3 ${field}`} value={filters.stato} onChange={(e) => setFilters((f) => ({ ...f, stato: e.target.value }))}>
          <option value="">Tutti gli stati</option>
          {Object.entries(STATI_VISITA).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
        </select>
        {perms.vedi_tutti ? (
          <select aria-label="Filtra per responsabile" className={`rounded-md border border-admin-border bg-transparent px-3 ${field}`} value={filters.responsabile} onChange={(e) => setFilters((f) => ({ ...f, responsabile: e.target.value }))}>
            <option value="">Tutti i commerciali</option>
            {(assignees.length ? assignees : responsabili).map((u) => <option key={u.email} value={u.email}>{u.nome || u.email}</option>)}
          </select>
        ) : null}
      </div>
      {loading ? (
        <div className="py-16 text-center text-admin-muted"><Loader2 className="mr-2 inline h-5 w-5 animate-spin" />Caricamento…</div>
      ) : !visits.length ? (
        <Card className={big ? "rounded-3xl border-admin-border bg-admin-surface" : ""}>
          <CardContent className="flex flex-col items-center gap-3 px-6 py-12 text-center">
            <CalendarCheck2 className="h-7 w-7 text-admin-accent" />
            <p className="text-lg font-medium text-admin-text">{filters.q || filters.stato || filters.responsabile ? "Nessuna visita con questi filtri" : "Non hai ancora visite"}</p>
            {perms.scrittura && !(filters.q || filters.stato) ? <p className="max-w-sm text-admin-muted">Cerca una struttura qui sopra per aprire la prima bozza.</p> : null}
          </CardContent>
        </Card>
      ) : (
        <div className={`grid gap-3 md:grid-cols-2 ${big ? "" : "xl:grid-cols-3"}`}>
          {visits.map((visit) => (
            <button type="button" key={visit.id} className="min-h-11 rounded-3xl border border-admin-border bg-admin-surface p-5 text-left shadow-sm hover:border-admin-accent" onClick={() => open(visit)}>
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 break-words text-lg font-semibold leading-snug">{visit.struttura_nome}</p>
                <StatoBadge stato={visit.stato} big={big} />
              </div>
              <p className="mt-2 text-admin-muted">{formatRome(visit.data_visita) || "Data da definire"}</p>
              <p className="mt-1 text-admin-text">{visitSummary(visit)}</p>
              {perms.vedi_tutti && visit.responsabile ? <p className="mt-1 text-sm text-admin-muted">{visit.responsabile.nome || visit.responsabile.email}</p> : null}
              {visit.prossimo_appuntamento && visit.stato !== "annullata" ? <p className="mt-2 text-sm text-admin-accent">Follow-up: {formatRome(visit.prossimo_appuntamento)}</p> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
